import React, { useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import SparkleIcon from "Components/common/SparkleIcon";
import ChatThread from "./components/ChatThread";
import Composer from "./components/Composer";
import TableSidePanel from "./components/TableSidePanel";
import SequencePreviewPanel from "./components/SequencePreviewPanel";
import EntityDetailPanel from "./components/EntityDetailPanel";
import CanvasResizer from "./components/CanvasResizer";
import HistoryDrawer from "./components/HistoryDrawer";
import ConversationLoader from "./components/ConversationLoader";
import { CopilotGreeting, CopilotCapabilities } from "./components/EmptyState";
import { markCopilotTried } from "Components/common/CopilotIntro";
import "./copilot.scss";

/**
 * /copilot — full-page agentic chat (ChatGPT-style) for prospecting
 * workflows. v1 runs against the scripted mock transport; see
 * agent/AgentTransport.js for the swap point to the real backend.
 */
const Copilot = observer(() => {
  const canvasRef = useRef(null);
  const { conversationId: routeId } = useParams();
  const history = useHistory();

  // Mount: mark tried, load the history drawer, and persist on the way out.
  useEffect(() => {
    // Reaching Copilot at all counts as "tried" — the dashboard intro popup
    // retires and stops re-appearing daily.
    markCopilotTried();
    copilotStore.loadConversations();
    if (!routeId) {
      // Bare /copilot -> canonical /copilot/new.
      history.replace("/copilot/new");
    }
    return () => {
      // keep the thread in history before the page state clears
      copilotStore.persistConversation();
      copilotStore.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to the URL: /copilot/new (or bare) -> fresh chat; /copilot/:id -> load it.
  useEffect(() => {
    if (routeId && routeId !== "new") {
      if (routeId !== copilotStore.conversationId) {
        copilotStore.loadConversation(routeId);
      }
    } else if (copilotStore.conversationId || !copilotStore.connected) {
      // /copilot/new: save the outgoing chat (snapshots synchronously) then
      // start fresh.
      copilotStore.persistConversation();
      copilotStore.reset();
      copilotStore.startConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // A fresh chat is created server-side lazily on its first message; reflect
  // its new id in the URL so /copilot/new becomes /copilot/:id (shareable,
  // reload-safe) without a navigation.
  useEffect(() => {
    if (
      copilotStore.conversationId &&
      (!routeId || routeId === "new")
    ) {
      history.replace(`/copilot/${copilotStore.conversationId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copilotStore.conversationId]);

  // The split width is user-dragged state, so it can't live in the stylesheet.
  // Push it in as a custom property and let SCSS do the actual styling.
  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.style.setProperty(
        "--copilotCanvasWidth",
        `${copilotStore.canvasWidth}px`
      );
    }
  }, [copilotStore.canvasWidth, copilotStore.canvasOpen]);

  return (
    <div className="copilotPage">
      <div className="copilotPage__header">
        <div className="copilotPage__headerLeft">
          <button
            type="button"
            className={`copilotPage__historyBtn ${
              copilotStore.historyOpen ? "active" : ""
            }`}
            title="Chat history"
            onMouseEnter={copilotStore.openHistoryHover}
            onMouseLeave={copilotStore.closeHistoryHover}
            onFocus={copilotStore.openHistoryHover}
          >
            <i className="material-icons-outlined">history</i>
          </button>
          <div className="copilotPage__headerTitle">
            <SparkleIcon />
            <span>Copilot</span>
          </div>
        </div>
        {copilotStore.messages.length > 0 && (
          <button
            type="button"
            className="copilotPage__newChatBtn"
            onClick={() => history.push("/copilot/new")}
            disabled={copilotStore.streaming}
          >
            <i className="material-icons-outlined">add</i>
            <span>New chat</span>
          </button>
        )}
      </div>
      <HistoryDrawer />
      {/*
        Chat and canvas sit side by side, each scrolling independently. The
        canvas docks rather than overlaying, so the conversation that produced
        a table stays readable next to it.
      */}
      <div className="copilotPage__body">
        {/*
          <Composer /> AND <ChatThread /> both stay mounted at a stable
          position across the hero <-> chat switch (toggled by className
          only, never by swapping wrapper element types). Two reasons:
          1. Unmounting the composer mid-flight destroys the textarea (and
             its focus) right as the response starts streaming in.
          2. <ChatThread /> is an `observer` — if it only mounts the instant
             the first message is pushed, it's a cold mount racing the same
             burst of turn_start/block_start/block_delta events that first
             message just kicked off, and can miss the mutations that land
             before its first commit. Keeping it mounted (it already
             `return`s null with zero messages) means its reaction has been
             live and warm since before any message ever existed.
        */}
        <div
          className={`copilotPage__main${
            copilotStore.messages.length === 0 ? " copilotPage__main--hero" : ""
          }`}
        >
          <ConversationLoader />
          {copilotStore.messages.length === 0 && <CopilotGreeting />}
          <div
            className={
              copilotStore.messages.length === 0 ? "" : "copilotPage__thread"
            }
          >
            <ChatThread />
          </div>
          {copilotStore.messages.length > 0 && !copilotStore.threadPinned && (
            <div className="copilotJumpAnchor">
              <button
                type="button"
                className="copilotJumpPill"
                onClick={copilotStore.requestThreadJump}
              >
                <i className="material-icons-outlined">arrow_downward</i>
                {copilotStore.streaming ? "New activity" : "Back to latest"}
              </button>
            </div>
          )}
          <div
            className={
              copilotStore.messages.length === 0
                ? "copilotHero__composer"
                : "copilotPage__composer"
            }
          >
            <Composer />
          </div>
          {copilotStore.messages.length === 0 && <CopilotCapabilities />}
        </div>
        {copilotStore.canvasOpen && (
          <>
            <CanvasResizer />
            <div className="copilotPage__canvas" ref={canvasRef}>
              {copilotStore.sidePanel.open && <TableSidePanel />}
              {copilotStore.previewPanel.open && <SequencePreviewPanel />}
              {copilotStore.entityPanel.open && <EntityDetailPanel />}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

export default Copilot;
